export type Severity = 'critical' | 'high' | 'moderate' | 'info';
export type Category =
  | 'safety'
  | 'structure'
  | 'water'
  | 'pest'
  | 'electrical'
  | 'legal'
  | 'exterior'
  | 'roof'
  | 'hvac'
  | 'environmental';

export const FINDING_CATEGORIES: { id: Category; label: string; icon: string }[] = [
  { id: 'safety', label: 'Safety', icon: 'ShieldAlert' },
  { id: 'structure', label: 'Structure', icon: 'Building2' },
  { id: 'water', label: 'Water', icon: 'Droplets' },
  { id: 'pest', label: 'Pest', icon: 'Bug' },
  { id: 'electrical', label: 'Electrical', icon: 'Zap' },
  { id: 'legal', label: 'Legal & Financial', icon: 'Scale' },
  { id: 'exterior', label: 'Exterior', icon: 'Trees' },
  { id: 'roof', label: 'Roof', icon: 'Home' },
  { id: 'hvac', label: 'HVAC', icon: 'Thermometer' },
  { id: 'environmental', label: 'Environmental', icon: 'Leaf' },
];

export interface Finding {
  id: string;
  severity: Severity;
  category: Category;
  title: string;
  page: number;
  doc: string;
  summary: string;
  est_low: number;
  est_high: number;
  action: string;
  cited_at: string;
}

export interface Property {
  id: string;
  address_line: string;
  address_city: string;
  neighborhood: string;
  price_range: string;
  beds: number;
  baths: number;
  sqft: number;
  year_built: number;
  status: string;
  days_on_market: number;
  summary_lede: string;
  pages_analyzed: number;
  docs_count: number;
  est_repair_low: number;
  est_repair_high: number;
  severity_counts: { critical: number; moderate: number; info: number };
  findings: Finding[];
  qa: { q: string; a: string; ts: string }[];
  offers: { from: string; amount: number; status: string; received: string; terms: string }[];
}

export const currentProperty: Property = {
  id: 'prop-1',
  address_line: '2847 Pacific Avenue',
  address_city: 'San Francisco, CA 94115',
  neighborhood: 'Pacific Heights',
  price_range: '$4.2M – $4.6M',
  beds: 4,
  baths: 3.5,
  sqft: 3120,
  year_built: 1923,
  status: 'Active',
  days_on_market: 6,
  summary_lede:
    'A 1923 Pacific Heights Victorian with a clean cosmetic package and three material issues buried in the disclosure packet. Two carry material cost exposure. One is likely negotiable.',
  pages_analyzed: 412,
  docs_count: 23,
  est_repair_low: 22500,
  est_repair_high: 38500,
  severity_counts: { critical: 1, high: 2, moderate: 3, info: 5 },
  findings: [
    {
      id: 'f1',
      severity: 'critical',
      category: 'structure',
      title: 'Foundation settlement, northeast corner',
      page: 87,
      doc: 'Structural Engineer Report.pdf',
      summary:
        'Inspector notes 7/8" differential settlement at the NE foundation corner with visible diagonal cracking in the basement wall. Recommended to engage a structural engineer for pier-and-beam evaluation before removing contingency.',
      est_low: 12000,
      est_high: 18000,
      action: 'Contingency hold',
      cited_at: 'Section 4.2, page 87',
    },
    {
      id: 'f2',
      severity: 'moderate',
      category: 'hvac',
      title: 'HVAC system past expected life',
      page: 34,
      doc: 'Home Inspection.pdf',
      summary:
        'Primary HVAC unit installed 2007. At 19 years, it is past the 15–20 year expected life. Still operating at time of inspection but end-of-life replacement should be priced in.',
      est_low: 4500,
      est_high: 7000,
      action: 'Credit or replace pre-close',
      cited_at: 'Section 7.1, page 34',
    },
    {
      id: 'f3',
      severity: 'moderate',
      category: 'exterior',
      title: 'Retaining wall, south boundary',
      page: 52,
      doc: 'Home Inspection.pdf',
      summary:
        'Rear retaining wall shows hairline cracking and ~2° lean. Not immediately failing but within the 5-year repair window. Neighbor-shared wall — coordinate before work.',
      est_low: 5500,
      est_high: 11500,
      action: '5-yr horizon',
      cited_at: 'Section 3.8, page 52',
    },
    {
      id: 'f4',
      severity: 'high',
      category: 'water',
      title: 'Sewer lateral, unverified compliance',
      page: 71,
      doc: 'NHD Report.pdf',
      summary:
        'Compliance certificate for the sewer lateral not on file. San Francisco requires upgrade at transfer if non-compliant. Scope an inspection (~$400) before closing.',
      est_low: 500,
      est_high: 2000,
      action: 'Inspect pre-close',
      cited_at: 'Section 9.3, page 71',
    },
    {
      id: 'f5',
      severity: 'info',
      category: 'roof',
      title: 'Roof replaced 2021',
      page: 18,
      doc: 'Home Inspection.pdf',
      summary:
        'Composition shingle roof replaced 2021 with 30-yr warranty. Warranty transferable to buyer.',
      est_low: 0,
      est_high: 0,
      action: 'No action',
      cited_at: 'Section 5.1, page 18',
    },
    {
      id: 'f6',
      severity: 'info',
      category: 'electrical',
      title: 'Electrical panel upgraded 2019',
      page: 22,
      doc: 'Home Inspection.pdf',
      summary: '200A panel upgrade completed 2019, permitted and inspected.',
      est_low: 0,
      est_high: 0,
      action: 'No action',
      cited_at: 'Section 6.2, page 22',
    },
    {
      id: 'f7',
      severity: 'high',
      category: 'safety',
      title: 'Missing smoke detectors on upper floor',
      page: 41,
      doc: 'Home Inspection.pdf',
      summary:
        'Two bedrooms on the upper floor lack hardwired smoke detectors. California requires interconnected detectors in every bedroom at transfer.',
      est_low: 300,
      est_high: 600,
      action: 'Install pre-close',
      cited_at: 'Section 8.1, page 41',
    },
    {
      id: 'f8',
      severity: 'moderate',
      category: 'environmental',
      title: 'Elevated moisture — basement',
      page: 56,
      doc: 'Home Inspection.pdf',
      summary:
        'Basement showed 18% moisture reading along the north wall. Not active mold, but above the 15% threshold. Recommend a moisture-remediation evaluation.',
      est_low: 1200,
      est_high: 3000,
      action: 'Remediation scope',
      cited_at: 'Section 5.4, page 56',
    },
    {
      id: 'f9',
      severity: 'info',
      category: 'legal',
      title: 'No outstanding permits on file',
      page: 12,
      doc: 'Preliminary Title Report.pdf',
      summary:
        'Title report shows no outstanding open permits, liens, or encumbrances beyond the standard mortgage.',
      est_low: 0,
      est_high: 0,
      action: 'No action',
      cited_at: 'Section 1.3, page 12',
    },
    {
      id: 'f10',
      severity: 'info',
      category: 'pest',
      title: 'Pest inspection clear',
      page: 4,
      doc: 'Pest Inspection.pdf',
      summary:
        'Licensed inspector found no active termite, dryrot, or wood-destroying organism activity at time of inspection. Section 1 clearance issued.',
      est_low: 0,
      est_high: 0,
      action: 'No action',
      cited_at: 'Section 1, page 4',
    },
    {
      id: 'f11',
      severity: 'info',
      category: 'structure',
      title: 'Garage slab, minor cracking',
      page: 63,
      doc: 'Home Inspection.pdf',
      summary:
        'Minor non-structural hairline cracking in garage slab, typical of settlement in homes of this age. Monitor only.',
      est_low: 0,
      est_high: 0,
      action: 'No action',
      cited_at: 'Section 4.7, page 63',
    },
  ],
  qa: [
    {
      q: 'Is this property in a fire hazard zone?',
      a: 'Yes, the NHD places the property in a State Responsibility Area moderate fire hazard zone. See page 42 of the NHD report.',
      ts: '2h ago',
    },
    {
      q: 'What is the HOA fee history?',
      a: 'No HOA — single-family home. Confirmed in the TDS, section 2.1.',
      ts: '4h ago',
    },
    {
      q: 'Are there any Mello-Roos special tax bonds?',
      a: 'No Mello-Roos are disclosed. Confirmed on page 31 of the NHD.',
      ts: '1d ago',
    },
  ],
  offers: [
    { from: 'Kim — Compass', amount: 4350000, status: 'Pending review', received: '2d ago', terms: '10-day inspection, 21-day close' },
    { from: 'Rodriguez — KW', amount: 4200000, status: 'Countered', received: '3d ago', terms: 'As-is, 30-day close' },
    { from: "Chen — Sotheby's", amount: 4425000, status: 'Pending review', received: '1d ago', terms: 'All-cash, 14-day close' },
  ],
};

export const propertiesList = [
  {
    id: 'prop-1',
    address_line: '2847 Pacific Avenue',
    city: 'Pacific Heights',
    status: 'Active',
    critical: 1,
    moderate: 3,
    info: 7,
    updated: '2h ago',
    est_low: 22500,
    est_high: 38500,
    price_range: '$4.2M – $4.6M',
    offers_count: 3,
    top_offer: 4425000,
    pages_analyzed: 412,
    docs_count: 23,
    days_on_market: 6,
    progress: 100,
  },
  {
    id: 'prop-2',
    address_line: '412 Dolores Street',
    city: 'Mission',
    status: 'Under contract',
    critical: 0,
    moderate: 2,
    info: 4,
    updated: '1d ago',
    est_low: 8000,
    est_high: 14000,
    price_range: '$1.9M – $2.1M',
    offers_count: 5,
    top_offer: 2050000,
    pages_analyzed: 286,
    docs_count: 17,
    days_on_market: 14,
    progress: 100,
  },
  {
    id: 'prop-3',
    address_line: '1501 Chestnut Street',
    city: 'Marina',
    status: 'Analyzing',
    critical: 0,
    moderate: 0,
    info: 0,
    updated: '12m ago',
    est_low: 0,
    est_high: 0,
    price_range: '$3.1M – $3.4M',
    offers_count: 0,
    top_offer: 0,
    pages_analyzed: 0,
    docs_count: 18,
    days_on_market: 0,
    progress: 62,
  },
  {
    id: 'prop-4',
    address_line: '88 Telegraph Hill Blvd',
    city: 'North Beach',
    status: 'Active',
    critical: 2,
    moderate: 4,
    info: 5,
    updated: '3d ago',
    est_low: 45000,
    est_high: 72000,
    price_range: '$5.8M – $6.4M',
    offers_count: 1,
    top_offer: 5900000,
    pages_analyzed: 538,
    docs_count: 31,
    days_on_market: 21,
    progress: 100,
  },
  {
    id: 'prop-5',
    address_line: '921 Castro Street',
    city: 'Noe Valley',
    status: 'Closed',
    critical: 0,
    moderate: 1,
    info: 3,
    updated: '2w ago',
    est_low: 3000,
    est_high: 6000,
    price_range: 'Sold $2.85M',
    offers_count: 7,
    top_offer: 2850000,
    pages_analyzed: 214,
    docs_count: 14,
    days_on_market: 9,
    progress: 100,
  },
];

export function formatMoney(n: number): string {
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'k';
  return '$' + n.toLocaleString();
}
